import React, { useEffect, useState } from 'react';
import { Button } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import { makeStyles } from '@material-ui/core/styles';
import styled from 'styled-components';
import { Space } from '../../../common';
import Layout from '../Layout';
import { useLanguage } from '../../../../context/language/language';
import useDeliveryData from '../../../common/hook/useDeliveryData';
import {
  DELIVERY_ADDRESS,
  SELECT_STORE,
} from '../../../../constant/deliveryFlow';
import useDeliveryControlFlow from '../../../common/hook/useDeliveryControlFlow';
import ClockIcon from '../../../../medias/icon/Component/ClockIcon';
import useResponsive from '../../../../utils/responsive';
import { useSite } from '../../../../context/siteContext';
import { defaultModalSize } from '../modalSize';

const Container = styled.div(() => ({
  display: 'flex',
  flexDirection: 'column',
}));

const ItemContainer = styled.div(({ theme }) => ({
  padding: 16,
  border: `1px solid ${theme.colors.grey100}`,
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
}));

const ItemHeader = styled.p(({ theme }) => ({
  ...theme.fonts.header6,
}));

const ItemDetail = styled.p(({ theme }) => ({
  ...theme.fonts.textS,
  color: theme.colors.grey550,
  width: theme.isMobile ? '100%' : '95%'
}));

const ItemChangeText = styled.p(({ theme }) => ({
  ...theme.fonts.textM,
  color: theme.colors.green600,
  fontWeight: 500,
  cursor: 'pointer',
}));

const ButtonContainer = styled.div(() => ({
  flex: 1,
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
  marginTop: 24,
}));

const BtnCancel = styled(Button)(({ theme }) => ({
  ...theme.radius.m,
  ...theme.fonts.header6,
  marginRight: 8,
  backgroundColor: theme.colors.grey75,
  height: 44,
  minWidth: 159,
  textTransform: 'none',
}));

const BtnSubmit = styled(Button)(({ theme, disabled }) => ({
  ...theme.radius.m,
  ...theme.fonts.header6,
  marginRight: 8,
  minWidth: 159,
  backgroundColor: theme.colors.green600,
  color: disabled ? theme.colors.grey500 : theme.colors.white,
  height: 44,
  textTransform: 'none',
  '&:hover': {
    backgroundColor: theme.colors.green400,
  },
}));

const useStyle = makeStyles((theme) => ({
  root: {
    flex: 1,
  },
  storeTextContainer: {
    marginTop: 8,
    borderRadius: 8,
    padding: 8,
    paddingLeft: 0,
    height: 'auto',
  },
  clock: {
    width: 16,
    height: 16,
    fill: '#F9BE00',
    marginRight: 8,
    paddingTop: 2,
  },
  storeText: {
    color: `${theme.yellow600} !important`,
  },
  changeText: {
    ...theme.fonts.textM,
    color: theme.green600,
    fontWeight: 500,
  },
  chevronRightIcon: {
    fill: theme.gray700,
    marginLeft: 8,
    fontSize: '1rem'
  }
}));

const StoreDetailLabel = ({
  changeHandler
}) => {
  const classes = useStyle();
  const word = useLanguage();
  const { MySite } = useSite();
  const { isMobile } = useResponsive();
  const deliveryData = useDeliveryData();
  return (
    <ItemContainer>
      <div style={{ flex: 1 }}>
        <ItemHeader>
          {deliveryData.currentStore.name}
        </ItemHeader>
        {/* <br /> */}
        <Grid container justify="space-between" alignItems="center" className={classes.storeTextContainer}>
          <Grid item style={{ flex: 1 }}>
            <Box display="flex">
              <ClockIcon className={classes.clock} />
              <ItemDetail className={classes.storeText}>{word('Store.Error.MissingInfo.Description')}</ItemDetail>
            </Box>
          </Grid>
          <Grid item style={{ width: isMobile ? '35%' : 'auto' }}>
            <Box display="flex" justifyContent="flex-end" alignItems="center" style={{ cursor: 'pointer' }} onClick={changeHandler}>
              <span className={classes.changeText}>{word('Slot.Button.Edit')}</span>
            </Box>
          </Grid>
        </Grid>
      </div>
    </ItemContainer>
  );
};

function DeliveryReOrderSummary(props) {
  const word = useLanguage();
  const { ThSite } = useSite();
  const deliveryData = useDeliveryData();
  const deliveryControl = useDeliveryControlFlow();
  const [titleHead, setTitleHead] = useState(word('DeliveryMethod.OnDemand'));
  const isOnDemand = deliveryData.deliveryType === 'OD';
  const { isMobile, isTablet, isTabletOrMobile } = useResponsive();

  useEffect(() => {
    if (ThSite) {
      if (deliveryData.deliveryMethod === 'HDND') {
        setTitleHead(word('DeliveryAddress.Type.Options.HomeDelivery'));
      } else if (deliveryData.deliveryMethod === 'CCND') {
        setTitleHead(word('DeliveryAddress.Type.Options.Collect'));
      }
    } else {
      if (isOnDemand) {
        setTitleHead(word('Header.Delivery.HDSD'));
      } else if (deliveryData.deliveryMethod === 'CC') {
        setTitleHead(word('OrderSummary.Contact.Pickup'));
      } else if (deliveryData.deliveryMethod === 'HD') {
        setTitleHead(word('DeliveryAddress.Type.Options.HomeDelivery'));
      }
    }
  }, [deliveryData.deliveryMethod]);

  return (
    <Layout
      title={titleHead}
      backButtonShown={deliveryControl.stackSize > 1}
      onBack={props.onBack}
      onClose={props.onClose}
      bottomSpace={isMobile ? 8 : isTablet ? 24 : 0}
      width={isTablet ? defaultModalSize.tablet.width : defaultModalSize.desktop.width}
      height={isTablet ? defaultModalSize.tablet.height : defaultModalSize.desktop.height}
    >
      <Container style={{ padding: isTabletOrMobile ? '0px 16px' : 0 }}>
        {deliveryData.shippingAddress.addressNumber && deliveryData.deliveryMethod.includes('HD') && (
          <>
            <SummaryItem
              header={word('Slot.HomeDelivery.Address')}
              desc={deliveryData.addressText}
              changeHandler={() => props.onNext(DELIVERY_ADDRESS)}
              word={word('Slot.Button.Edit')}
              id="edit-delivery-address"
            />
            <Space line={0.8} />
          </>
        )}
        <StoreDetailLabel changeHandler={() => props.onNext(SELECT_STORE)} />
        <ButtonContainer>
          <BtnCancel
            id="cancel-delivery-summary-btn"
            data-cta-name-en="Cancel"
            data-cta-name-th="ยกเลิก"
            data-cta-type="Button"
            onClick={props.onClose}
          >
            {word('General.Cancel')}
          </BtnCancel>
        </ButtonContainer>
      </Container>
    </Layout>
  );
}

export function SummaryItem({
  header, desc, changeHandler, word, showChange = true, id
}) {
  return (
    <ItemContainer>
      <div style={{ flex: 1 }}>
        <ItemHeader>{header}</ItemHeader>
        {/* <br /> */}
        <ItemDetail>{desc}</ItemDetail>
      </div>
      { showChange && (
        <ItemChangeText id={id} onClick={changeHandler}>{word}</ItemChangeText>
      )}
    </ItemContainer>
  );
}

export default DeliveryReOrderSummary;
